import { scheduleApi } from '@/lib/api/schedule';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
const SCHEDULE_ITEMS_KEY = ['schedule', 'items'];
/**
 * How often the schedule board re-reads the server on its own.
 *
 * Bookings arrive from other people: a colleague adding a patient in another browser, an order
 * pushed by the practice management system, a modality worklist query. None of those touch this
 * browser's query cache, so without a cadence the board only changed after the current user's own
 * edits (which invalidate the keys below) or a manual reload. The worklist already polls every
 * 25 s for the same reason; the schedule is what the front desk watches, so it polls a little
 * faster. Polling pauses while the tab is hidden and a refetch also fires when the tab regains
 * focus, so a board left open overnight catches up the moment someone looks at it.
 */
export const SCHEDULE_REFETCH_INTERVAL_MS = 20_000;
const SCHEDULE_ITEM_KEY = (id) => ['schedule', 'item', id];
const SCHEDULE_MODALITIES_KEY = ['schedule', 'modalities'];
const SCHEDULE_CONFIGURATION_KEY = (clinicId) => ['schedule', 'configuration', clinicId];
/**
 * Defaults mirroring the server's documented response for a clinic with no
 * configuration row yet, so a consumer can render before the query resolves
 * instead of guarding on `undefined` everywhere.
 */
export const DEFAULT_SCHEDULE_CONFIGURATION = {
    mwlAeTitle: null,
    mwlEnabled: false,
    schedulingEnabled: true,
    workingHoursStart: '07:00',
    workingHoursEnd: '19:00',
    slotDurationMinutes: 30,
    defaultPriority: 'ROUTINE',
    allowOverbooking: false,
    showConflictWarning: true,
    accessionPrefix: '',
    requiredFields: ['patientName', 'patientId'],
    defaultEmailRecipients: [],
};
/**
 * The grid's first and last hour, from the configuration's "HH:mm" strings.
 *
 * Falls back to the 07:00-19:00 day rather than to hour 0 when a stored value cannot be parsed:
 * a grid that silently starts at midnight reads as a rendering fault, and every clinic that has
 * never opened the settings panel has whatever the server's defaults say.
 */
export function workingHoursFromConfiguration(config) {
    // The server rejects a non-zero minutes component on save, so every stored value is already on
    // the hour -- taking just the hour part here never discards real data.
    const start = parseInt(config.workingHoursStart.split(':')[0], 10);
    const end = parseInt(config.workingHoursEnd.split(':')[0], 10);
    return { startHour: isNaN(start) ? 7 : start, endHour: isNaN(end) ? 19 : end };
}
function getDayRange(date) {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return { begin: start.toISOString(), end: end.toISOString() };
}
export function useScheduleItems({ clinicId, viewDate, modalityId, statusFilter, accessionSearch, queuedOnly, }) {
    const range = getDayRange(viewDate);
    const modalityIdParam = modalityId && modalityId !== 'all' && modalityId.length > 8 ? modalityId : undefined;
    const { data, isLoading, error } = useQuery({
        queryKey: [
            ...SCHEDULE_ITEMS_KEY,
            clinicId,
            range.begin,
            range.end,
            modalityIdParam,
            statusFilter,
            accessionSearch ?? '',
            queuedOnly ?? false,
        ],
        queryFn: () => scheduleApi.getItems({
            beginDate: range.begin,
            endDate: range.end,
            modalityId: modalityIdParam,
            queued: queuedOnly,
            ...(clinicId ? { clinicId } : {}),
        }),
        enabled: !!clinicId && !!range.begin && !!range.end,
        // See SCHEDULE_REFETCH_INTERVAL_MS. staleTime 0 (not the client's 5-minute default) is what
        // lets the focus refetch and a remount actually hit the server instead of serving the cache.
        refetchInterval: SCHEDULE_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    const items = (data ?? []);
    const filteredByModalityType = modalityId && modalityId !== 'all' && modalityId.length <= 8
        ? items.filter((i) => i.modalityType === modalityId)
        : items;
    const filteredByStatus = statusFilter && statusFilter !== 'all'
        ? filteredByModalityType.filter((i) => i.scheduleStatus === statusFilter)
        : filteredByModalityType;
    const filteredByAccession = accessionSearch
        ? filteredByStatus.filter((i) => {
            const needle = accessionSearch.toLowerCase();
            return (i.accessionNumber.toLowerCase().includes(needle) ||
                (i.externalReferenceId?.toLowerCase().includes(needle) ?? false));
        })
        : filteredByStatus;
    return { items: filteredByAccession, isLoading, error };
}
/**
 * Orders a PMS sent that could not be routed to a room.
 *
 * Deliberately NOT date-scoped, and the server ignores the date parameters for this query. An
 * unrouted order needs a human to assign a room, and that attention does not reliably happen the
 * same day -- so a date-windowed queue would let one go quietly stale the moment the board rolled
 * over to tomorrow, which is the exact failure this whole feature exists to end.
 */
export function useUnassignedScheduleItems(clinicId) {
    const { data, isLoading, error } = useQuery({
        queryKey: [...SCHEDULE_ITEMS_KEY, 'unassigned', clinicId],
        queryFn: () => scheduleApi.getItems({
            // Required by the endpoint's signature but ignored when unassigned is set. Sent as a wide
            // window rather than today so a stricter server would still return the backlog.
            beginDate: new Date(0).toISOString(),
            endDate: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString(),
            unassigned: true,
            ...(clinicId ? { clinicId } : {}),
        }),
        enabled: !!clinicId,
        // Unrouted orders only ever arrive from the PMS, never from this browser, so this queue is
        // the one that most needs the cadence.
        refetchInterval: SCHEDULE_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    return { items: (data ?? []), isLoading, error };
}
export function useScheduleModalities(clinicId) {
    return useQuery({
        queryKey: [...SCHEDULE_MODALITIES_KEY, clinicId],
        queryFn: () => scheduleApi.getModalities(clinicId ? { clinicId } : undefined),
        enabled: !!clinicId,
    });
}
/**
 * Creates a schedule item. The mutation resolves with the server's ScheduleItemCreateResult, so an
 * `onSuccess` callback can read `overlapsExisting` and warn that the booking double-booked a slot
 * on a clinic that permits it — the operator would otherwise find out at the modality.
 */
export function useCreateScheduleItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (req) => scheduleApi.createItem(req),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
        },
    });
}
export function useUpdateScheduleItem() {
    const queryClient = useQueryClient();
    return useMutation({
        // ScheduleItemUpdate, not Partial<ScheduleItem>: the PUT is a sparse patch where an absent
        // field means "leave this column alone", so unassigning needs the explicit clear flags. Typing
        // this as Partial would hide them and fail the excess-property check on an inline literal.
        mutationFn: ({ id, item }) => scheduleApi.updateItem(id, item),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEM_KEY(id) });
        },
    });
}
export function useCancelScheduleItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, cancelReason }) => scheduleApi.cancelItem(id, cancelReason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
        },
    });
}
export function useUpdateScheduleStatus() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status, }) => scheduleApi.updateStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
        },
    });
}
export function useBreakMpps() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => scheduleApi.breakMpps(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
        },
    });
}
export function useMarkBilled() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status, }) => scheduleApi.updateStatus(id, status, true),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
        },
    });
}
export function useSaveModality() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (modality) => scheduleApi.saveModality(modality),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_MODALITIES_KEY });
        },
    });
}
export function useTelemedicineOptions(clinicId) {
    return useQuery({
        queryKey: ['schedule', 'telemedicine-options', clinicId],
        queryFn: () => scheduleApi.getTelemedicineOptions(clinicId ? { clinicId } : undefined),
        enabled: !!clinicId,
    });
}
export function useScheduleConfiguration(clinicId) {
    return useQuery({
        queryKey: SCHEDULE_CONFIGURATION_KEY(clinicId),
        queryFn: () => scheduleApi.getConfiguration(clinicId),
        enabled: !!clinicId,
        staleTime: 60 * 1000,
    });
}
export function useUpdateScheduleConfiguration() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ clinicId, body, }) => scheduleApi.updateConfiguration(clinicId, body),
        onSuccess: (_, { clinicId }) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULE_CONFIGURATION_KEY(clinicId) });
        },
    });
}
